import { CLOSE_RELATIVE_NOTES, CONTACT_NOTES, ContactNote, isCloseRelative } from '@asko/shared';
import { formatPhone } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { Select, type SelectOption } from './Select';

/**
 * Qo'shimcha kontaktlar (BT §6.6.1, Alif `additional-data`).
 *
 * Alif uchta qoida qo'yadi: kamida 2 ta kontakt, kamida bittasi yaqin
 * qarindosh, va raqam mijozning o'z raqami bilan bir xil bo'lmasligi kerak.
 * Uchalasini ham shu yerda ko'rsatamiz — sotuvchi xatoni scoring paytida
 * emas, anketa to'ldirayotganda ko'rishi kerak.
 *
 * Backend xatolari qator bo'yicha keladi (`contacts.1.phone`) — ular ham
 * o'sha qatorning tagida chiqadi, aks holda «xatolik bor» deyilgani bilan
 * qayerdaligi ko'rinmaydi.
 */

export interface ContactDraft {
  note: ContactNote;
  name: string;
  phone: string;
}

/** Raqam bo'sh boshlanadi: oldindan yozilgan «998» ikkita qatorni bir xil
 *  qilib qo'yardi va sotuvchi hech narsa kiritmasdan «raqamlar takrorlanmasin»
 *  degan xatoni ko'rardi. */
export const EMPTY_CONTACT: ContactDraft = { note: ContactNote.PARENT, name: '', phone: '' };

interface ContactsFieldsProps {
  contacts: ContactDraft[];
  clientPhone: string;
  onChange: (contacts: ContactDraft[]) => void;
  /** Butun ro'yxatga tegishli xato (`contacts`). */
  error?: string | undefined;
  /** Bitta maydon xatosi: `contacts.0.phone` kabi. */
  fieldError: (name: string) => string | undefined;
}

const MAX_CONTACTS = 5;

const OTHER_NOTES = (Object.keys(CONTACT_NOTES) as ContactNote[]).filter(
  (note) => !isCloseRelative(note),
);

export function ContactsFields({
  contacts,
  clientPhone,
  onChange,
  error,
  fieldError,
}: ContactsFieldsProps) {
  const { t } = useI18n();

  const hasCloseRelative = contacts.some((c) => isCloseRelative(c.note));
  const ownPhoneUsed = contacts.some(
    (c) => c.phone.length === 12 && c.phone === clientPhone.replace(/\D/g, ''),
  );

  function update(index: number, patch: Partial<ContactDraft>) {
    onChange(contacts.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  }

  const noteLabel = (note: ContactNote) => t(`note.${note}`);

  // Yaqin qarindoshlar birinchi guruhda: Alif kamida bittasini talab
  // qiladi, shuning uchun ular ko'zga birinchi tashlanishi kerak.
  const noteOptions: Array<SelectOption<ContactNote>> = [
    ...CLOSE_RELATIVE_NOTES.map((note) => ({
      value: note,
      label: noteLabel(note),
      group: t('contacts.groupClose'),
    })),
    ...OTHER_NOTES.map((note) => ({
      value: note,
      label: noteLabel(note),
      group: t('contacts.groupOther'),
    })),
  ];

  return (
    <fieldset className="contacts">
      <legend className="field__label">{t('contacts.legend')}</legend>
      <p className="contacts__hint">{t('contacts.hint')}</p>

      {contacts.map((contact, index) => {
        const nameError = fieldError(`contacts.${index}.name`);
        const phoneError = fieldError(`contacts.${index}.phone`);

        return (
          <div className="contacts__item" key={index}>
            <div className="contacts__row">
              <Select
                className="contacts__note"
                ariaLabel={t('contacts.type', { n: index + 1 })}
                value={contact.note}
                options={noteOptions}
                onChange={(note) => update(index, { note })}
              />

              <input
                className="field__input"
                aria-label={t('contacts.name', { n: index + 1 })}
                aria-invalid={Boolean(nameError)}
                placeholder={t('contacts.namePlaceholder')}
                value={contact.name}
                onChange={(e) => update(index, { name: e.target.value })}
              />

              <input
                className="field__input field__input--mono"
                aria-label={t('contacts.phone', { n: index + 1 })}
                aria-invalid={Boolean(phoneError)}
                inputMode="numeric"
                placeholder="998 90 123 45 67"
                value={formatPhone(contact.phone)}
                onChange={(e) =>
                  update(index, { phone: e.target.value.replace(/\D/g, '').slice(0, 12) })
                }
              />

              {contacts.length > 2 && (
                <button
                  type="button"
                  className="btn btn--small contacts__remove"
                  aria-label={t('contacts.remove', { n: index + 1 })}
                  onClick={() => onChange(contacts.filter((_, i) => i !== index))}
                >
                  ✕
                </button>
              )}
            </div>

            {(nameError || phoneError) && (
              <div className="contacts__errors">
                {nameError && <span className="field__error">{nameError}</span>}
                {phoneError && <span className="field__error">{phoneError}</span>}
              </div>
            )}
          </div>
        );
      })}

      {contacts.length < MAX_CONTACTS && (
        <button
          type="button"
          className="btn btn--small"
          onClick={() => onChange([...contacts, { ...EMPTY_CONTACT, note: ContactNote.FRIEND }])}
        >
          {t('contacts.add')}
        </button>
      )}

      {!hasCloseRelative && <span className="field__error">{t('contacts.needClose')}</span>}
      {ownPhoneUsed && <span className="field__error">{t('contacts.ownPhone')}</span>}
      {error && <span className="field__error">{error}</span>}
    </fieldset>
  );
}
